import React, { useContext, useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import {
    Button,
    BusinessDashboardOverview,
    LottieLoadingIndicator,
    BusinessPortalHeader,
    PortalActionBar,
    PortalCaption,
    PortalKicker,
    PortalIconActionButton,
    PortalIconActionButtonIcon,
    PortalLoadingState,
    PortalMetricCard,
    PortalMetricGrid,
    PortalMetricLabel,
    PortalMetricValue,
    PortalPanelHeader,
    PortalSectionTitle,
    PortalSurface,
    PortalSurfaceStack,
    PortalWorkspace,
} from "src/components";
import { SubscriptionContext } from "../SubscriptionContext";
import Icon from "../../../img/new/purchase_more_users.svg";
import { UserContext } from "src/scenes/App/UserContext";
import * as PERMISSIONS from "src/constants/permissions";
import { FEATURE_FLAGS, hasFeature } from "src/constants/FeatureFlag";
import { withRouter } from "react-router-dom";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import { SubscriptionMode, UserKeys } from "src/constants/userDetails";
import ClientRequiredDocumentsModal from "./ClientRequiredDocumentsModal";
import TierSelectionModal from "./TierSelectionModal";
import { EnumLicenseStatus } from "src/scenes/ClientLicense/components/ClientLicenseTable";
import EmployeeRequiredDocumentsModal from "./EmployeeRequiredDocumentsModal";
import { getDashboardOverviewData } from "../api/dashboardOverview";

const CompanyBadge = styled.div`
    min-height: 42px;
    max-width: 28rem;
    display: inline-flex;
    align-items: center;
    padding: 0 1rem;
    color: #171A22;
    background: #F3F5FB;
    border: 1px solid #D8DEEC;
    border-radius: 8px;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DashboardActionCopy = styled.div`
    min-width: 0;
`;

const DashboardAlert = styled(PortalSurface)`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 1rem;
    background: ${p => p.$isExpiry ? "#FFF5F5" : "#F3F5FB"};
    border-color: ${p => p.$isExpiry ? "#F3B5B5" : "#D8DEEC"};
    box-shadow: none;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

const DashboardUserMetricCard = styled(PortalMetricCard)`
    background:
        linear-gradient(${p => p.$soft || "#F3F5FB"}, ${p => p.$soft || "#F3F5FB"}) padding-box,
        linear-gradient(135deg, ${p => p.$from || "#C9D2E7"}, ${p => p.$to || "#171A22"}) border-box;
    border: 1px solid transparent;
    box-shadow: none;
`;

const emptyOverviewData = {
    quote: null,
    mood: null,
    moodHistory: [],
    notice: null,
    notices: [],
    survey: null,
    surveys: [],
    poll: null,
    polls: [],
    perks: [],
};

const emptyOverviewAvailability = {
    quote: false,
    mood: false,
    notice: false,
    survey: false,
    poll: false,
    perks: false,
};

const SubscriptionIssuesAlert = ({
    isExpiry,
    onClickIsExpiredHandler,
    presentActivateBusinessPrompt
}) => {
    const header = isExpiry ?
        null :
        "Action Required: Activate Your Me Business Account";
    const mainText = isExpiry ?
        "Your subscription has expired. Please make a payment immediately to prevent cancellation. Go to Account Settings > Subscriptions to view." :
        "Complete your initial subscription and user license payment to unlock full access and start building your platform today. Finalise your payment now to unlock your platform and begin inviting your team or clients.";
    const buttonText = isExpiry ?
        "View" :
        "Pay Now";
    return (
        <DashboardAlert $isExpiry={isExpiry} padding="1rem">
            <DashboardActionCopy>
                { header &&
                    <PortalKicker color="#171A22">
                        { header }
                    </PortalKicker>
                }
                <PortalSectionTitle size="0.95rem" color={isExpiry ? "#D64545" : "#171A22"}>
                    Subscriptions
                </PortalSectionTitle>
                <PortalCaption color={isExpiry ? "#B93A3A" : "#6A7387"} maxWidth="52rem">
                    { mainText }
                </PortalCaption>
            </DashboardActionCopy>
            <Button
                color={ isExpiry ? "#FF5353" : "white" }
                backgroundColor={ isExpiry ? "transparent" : "#171A22" }
                border={ isExpiry ? "1px solid #FF5353" : "1px solid #171A22" }
                width="6.4375rem"
                height="42px"
                borderRadius="8px"
                size="0.875rem"
                padding="0"
                style={{ fontWeight: 700 }}
                onClick={isExpiry ? onClickIsExpiredHandler : presentActivateBusinessPrompt}
            >
                { buttonText }
            </Button>
        </DashboardAlert>
    );
};

const BusinessDashboard = ({ ...props }) => {
    const {
        companyName,
        totalNumber,
        employees,
        clients,
        trialClientUsers,
        isSalesAndPromotionsEnabled,
        isSubscriptionExpired,
        companyMode,
        goToPurchaseMoreUsers,
        loadingCount,
    } = useContext(SubscriptionContext);
    const user = useContext(UserContext);
    const { permissions, [UserKeys.SUBSCRIPTION_WAS_EVER_PAID]: subscriptionWasEverPaid, requiredDocuments, appFns } = user;
    const { presentActivateBusinessPrompt, licenseTier } = props;

    const hasBillingPermission = PERMISSIONS.hasPermissions([PERMISSIONS.BILLING], permissions);
    const hasUserManagementPermission = PERMISSIONS.hasPermissions([PERMISSIONS.USER_LISTS], permissions);
    const hasDashboardManagementAccess = hasBillingPermission || hasUserManagementPermission;
    const isInDemoMode = companyMode === SubscriptionMode.DEMO;
    const subscriptionIssuesNeedResolution = !isInDemoMode && (isSubscriptionExpired || !subscriptionWasEverPaid);
    const purchaseUsersAvailable = !isInDemoMode && !subscriptionIssuesNeedResolution;
    const showUserNumbers = isInDemoMode || subscriptionWasEverPaid;
    const [showEmployeeDocsModal, setShowEmployeeDocsModal] = useState(false);
    const [showDocsModal, setShowDocsModal] = useState(true);
    const [dismissedTierModal, setDismissedTierModal] = useState(false);
    const [overviewData, setOverviewData] = useState(emptyOverviewData);
    const [isOverviewLoading, setIsOverviewLoading] = useState(false);

    const overviewAvailability = {
        quote: hasFeature(user, FEATURE_FLAGS.QUOTES) || PERMISSIONS.hasAnyPermission([PERMISSIONS.MY_QUOTE], permissions),
        mood: hasFeature(user, FEATURE_FLAGS.MOOD_TRACKER),
        notice: hasFeature(user, FEATURE_FLAGS.NOTICEBOARD) || PERMISSIONS.hasAnyPermission([PERMISSIONS.NOTICEBOARD, PERMISSIONS.CLIENT_NOTICEBOARD], permissions),
        survey: hasFeature(user, FEATURE_FLAGS.SURVEYS) || PERMISSIONS.hasAnyPermission([PERMISSIONS.SURVEYS], permissions),
        poll: hasFeature(user, FEATURE_FLAGS.POLLS) || PERMISSIONS.hasAnyPermission([PERMISSIONS.POLLS], permissions),
        perks: hasFeature(user, FEATURE_FLAGS.PERKS) || PERMISSIONS.hasAnyPermission([PERMISSIONS.PERKS], permissions),
    };
    const overviewRequestKey = useMemo(() => JSON.stringify({
        token: user?.[UserKeys.TOKEN],
        companyName: user?.[UserKeys.COMPANY_NAME],
        isClient: user?.[UserKeys.IS_CLIENT],
        quoteFeatureName: user?.[UserKeys.CUSTOM_NAME_MY_QUOTES],
        availability: overviewAvailability,
    }), [
        user?.[UserKeys.TOKEN],
        user?.[UserKeys.COMPANY_NAME],
        user?.[UserKeys.IS_CLIENT],
        user?.[UserKeys.CUSTOM_NAME_MY_QUOTES],
        overviewAvailability.quote,
        overviewAvailability.mood,
        overviewAvailability.notice,
        overviewAvailability.survey,
        overviewAvailability.poll,
        overviewAvailability.perks,
    ]);

    const hasLicense = !!licenseTier;
    const hasRequiredDocs = (licenseTier?.requiredDocuments || []).length > 0;
    const isActive =
        props.currentTerms?.licenseStatus === EnumLicenseStatus.ACTIVE ||
        licenseTier?.licenseStatus === EnumLicenseStatus.ACTIVE;

    const shouldShowDocsModal =
        hasLicense &&
        hasRequiredDocs &&
        isActive &&
        !licenseTier?.requiredDocuments?.every(d => d.upload);

    const shouldAutoShowEmployeeDocsModal =
        requiredDocuments?.length > 0 &&
        !requiredDocuments.every(d => d.upload?.url);

    const shouldShowTierSelectionModal =
        (props.showTierSelectionModal || props.reopenTierModal) &&
        !props.currentTerms?.licenseTier &&
        !isActive && !dismissedTierModal;

    const licenses = [
        { label: "Total Licenses Purchased", number: totalNumber },
        { label: "Total Licenses Used", number: employees + clients },
        { label: "Total Licenses Remaining", number: isInDemoMode ? "-" : totalNumber - (employees + clients) },
    ];

    useEffect(() => {
        if (shouldAutoShowEmployeeDocsModal) {
            setShowEmployeeDocsModal(true);
        }
    }, [shouldAutoShowEmployeeDocsModal]);

    useEffect(() => {
        const controller = new AbortController();
        const token = user?.[UserKeys.TOKEN];

        if (!token) {
            setOverviewData(emptyOverviewData);
            setIsOverviewLoading(false);
            return () => controller.abort();
        }

        const fetchOverview = async () => {
            setIsOverviewLoading(true);
            const nextData = await getDashboardOverviewData({
                user,
                availability: overviewAvailability,
                signal: controller.signal
            });

            if (!controller.signal.aborted) {
                setOverviewData(nextData);
                setIsOverviewLoading(false);
            }
        };

        fetchOverview().catch(error => {
            if (error.name !== "CanceledError") {
                console.error("Unable to load dashboard overview.", error);
            }
            if (!controller.signal.aborted) {
                setOverviewData(emptyOverviewData);
                setIsOverviewLoading(false);
            }
        });

        return () => controller.abort();
    }, [overviewRequestKey]);

    let userCards = [
        {
            label: `${capitalizeString(pluralize(customNames.employees))}`,
            number: employees,
            color: "#26A26B",
            soft: "#F0FBF7",
            from: "#26A26B",
            to: "#7DD3B0"
        },
        {
            label: `${capitalizeString(pluralize(customNames.clients))}`,
            number: clients,
            color: "#8551AE",
            soft: "#F6F1FF",
            from: "#8551AE",
            to: "#C4A2FF"
        },
        // { label: 'Admins', number: '10', background: '#EFE7FF', border: '#633FE4', color: '#633FE4' },
        // { label: 'Support Professional', number: '10', background: '#E6FFF8', border: '#26A26B', color: '#26A26B' },
    ];

    if (isSalesAndPromotionsEnabled) {
        userCards.push({
            label: "Others",
            number: trialClientUsers,
            color: "#D98B2B",
            soft: "#FFF7E8",
            from: "#D98B2B",
            to: "#F7C96A"
        });
    }

    const licensesToRender = licenses.map((license, index) => {
        let number = typeof license.number === "number" ? Math.max(0, license.number) : license.number;
        number = showUserNumbers ? number : 0;
        return (
            <PortalMetricCard key={index} $delay={`${index * 0.035}s`}>
                <PortalMetricLabel>{license.label}</PortalMetricLabel>
                <PortalMetricValue>{number}</PortalMetricValue>
            </PortalMetricCard>
        );
    });

    const userCardsToRender = userCards.map((user, index) => {
        const number = showUserNumbers ? user.number : 0;
        return (
            <DashboardUserMetricCard
                key={index}
                $soft={user.soft}
                $from={user.from}
                $to={user.to}
                $delay={`${index * 0.045}s`}
            >
                <PortalMetricLabel color={user.color}>{user.label}</PortalMetricLabel>
                <PortalMetricValue color={user.color}>{number}</PortalMetricValue>
            </DashboardUserMetricCard>
        );
    });

    const onClickIsExpiredHandler = () => {
        props.history.push("/setting/subscriptions");
    };

    const openDashboardOverviewFeature = item => {
        const feature = item?.source?.automation?.feature;
        const sourceReferenceId = item?.automationDetail?.sourceReferenceId || item?.source?.automation?.sourceReferenceId || item?.uuid;

        if (feature === "noticeboard") {
            props.history.push(`/noticeboard?noticeboard=${item?.audience === "clients" ? "client" : "employee"}`);
        }

        if (feature === "survey" && sourceReferenceId) {
            props.history.push(`/survey?viewSurveyUuid=${encodeURIComponent(sourceReferenceId)}`);
        }

        if (feature === "poll" && sourceReferenceId) {
            props.history.push(`/survey?viewPollUuid=${encodeURIComponent(sourceReferenceId)}`);
        }
    };

    return (
        <>
            <BusinessPortalHeader
                title="Business Dashboard"
                description="Track subscription health, license capacity, and user activity from one operational overview."
            >
                <CompanyBadge title={companyName}>{ companyName.length > 50 ? companyName.substring(0, 49) + "..." : companyName }</CompanyBadge>
            </BusinessPortalHeader>
            { subscriptionIssuesNeedResolution &&
                <SubscriptionIssuesAlert
                    isExpiry={isSubscriptionExpired}
                    onClickIsExpiredHandler={onClickIsExpiredHandler}
                    presentActivateBusinessPrompt={presentActivateBusinessPrompt}
                />
            }
            <BusinessDashboardOverview
                data={overviewData}
                loading={isOverviewLoading}
                availability={overviewAvailability || emptyOverviewAvailability}
                onViewFeature={openDashboardOverviewFeature}
                quoteFeatureName={user?.[UserKeys.CUSTOM_NAME_MY_QUOTES]}
            />
            {hasDashboardManagementAccess && (
                <LoadingContainer loadingCount={loadingCount}>
                    <PortalWorkspace columns="minmax(0, 1fr) 19rem" breakpoint="1180px">
                        <PortalSurfaceStack>
                            { hasBillingPermission &&
                            <PortalSurface padding="1rem">
                                <PortalPanelHeader withDivider>
                                    <div>
                                        <PortalKicker>Licenses</PortalKicker>
                                        <PortalSectionTitle>License Overview</PortalSectionTitle>
                                        <PortalCaption>Understand purchased, used, and remaining license capacity.</PortalCaption>
                                    </div>
                                </PortalPanelHeader>
                                <PortalMetricGrid>
                                    { licensesToRender }
                                </PortalMetricGrid>
                                { !isInDemoMode && (
                                    <PortalActionBar shadow="none" margin="1rem 0 0" style={{ marginTop: "1rem" }}>
                                        <DashboardActionCopy>
                                            <PortalSectionTitle size="0.95rem">Need more capacity?</PortalSectionTitle>
                                            <PortalCaption margin="0.25rem 0 0">
                                                Purchase additional user licenses and manage the subscriptions later in Account Settings.
                                            </PortalCaption>
                                        </DashboardActionCopy>
                                        <PortalIconActionButton
                                            backgroundColor={ purchaseUsersAvailable ? undefined : "#bfbfbf" }
                                            border={ purchaseUsersAvailable ? undefined : "1px solid #bfbfbf" }
                                            color="#fff"
                                            height="42px"
                                            borderRadius="8px"
                                            padding="0 1rem"
                                            width="auto"
                                            style={{
                                                cursor: purchaseUsersAvailable ? "pointer" : "default",
                                                pointerEvents: purchaseUsersAvailable ? "auto" : "none"
                                            }}
                                            onClick={goToPurchaseMoreUsers}
                                        >
                                            <PortalIconActionButtonIcon src={Icon} />
                                            Purchase More User Licenses
                                        </PortalIconActionButton>
                                    </PortalActionBar>
                                )}
                            </PortalSurface>
                            }
                          

                            { hasUserManagementPermission &&
                            <PortalSurface padding="1rem">
                                <PortalPanelHeader withDivider>
                                    <div>
                                        <PortalKicker>People</PortalKicker>
                                        <PortalSectionTitle>User Breakdown</PortalSectionTitle>
                                        <PortalCaption>Monitor active user groups available to this portal.</PortalCaption>
                                    </div>
                                </PortalPanelHeader>
                                <PortalMetricGrid columns={isSalesAndPromotionsEnabled ? 3 : 2}>
                                    { userCardsToRender }
                                </PortalMetricGrid>
                            </PortalSurface>
                            }

                        </PortalSurfaceStack>
                        <PortalSurfaceStack>
                            {hasUserManagementPermission && (
                                <PortalSurface padding="1rem">
                                    <PortalKicker>Status</PortalKicker>
                                    <PortalSectionTitle>Account Snapshot</PortalSectionTitle>
                                    <PortalCaption>Current subscription mode and document actions.</PortalCaption>
                                    <PortalMetricCard style={{ marginTop: "1rem" }}>
                                        <PortalMetricLabel>Company Mode</PortalMetricLabel>
                                        <PortalMetricValue size="1.35rem">{isInDemoMode ? "Demo" : "Live"}</PortalMetricValue>
                                    </PortalMetricCard>
                                </PortalSurface>
                            )}
                            { requiredDocuments?.length > 0 && (
                                <PortalSurface padding="1rem">
                                    <PortalKicker>Documents</PortalKicker>
                                    <PortalSectionTitle>My Submitted Documents</PortalSectionTitle>
                                    <PortalCaption>Review and manage documents submitted for this account.</PortalCaption>
                                    <PortalMetricCard style={{ marginTop: "1rem" }}>
                                        <PortalMetricLabel>Documents Available</PortalMetricLabel>
                                        <PortalMetricValue>{requiredDocuments?.length}</PortalMetricValue>
                                    </PortalMetricCard>
                                    <Button
                                        backgroundColor="#171A22"
                                        color="#FFFFFF"
                                        width="6.4375rem"
                                        height="42px"
                                        borderRadius="8px"
                                        marginTop="1rem"
                                        padding="0"
                                        onClick={() => setShowEmployeeDocsModal(true)}
                                    >
                                    View
                                    </Button>
                                </PortalSurface>
                            ) }
                        </PortalSurfaceStack>
                    </PortalWorkspace>
                </LoadingContainer>
            )}
            { shouldShowDocsModal && (
                <ClientRequiredDocumentsModal
                    open={showDocsModal}
                    businessName={companyName}
                    requiredDocuments={licenseTier?.requiredDocuments || []}
                    onClose={() => setShowDocsModal(false)}
                    headerTitle={"Upload Required Documents"}
                />
            )}

            <EmployeeRequiredDocumentsModal
                open={showEmployeeDocsModal}
                onClose={() => setShowEmployeeDocsModal(false)}
                businessName={companyName}
                requiredDocuments={requiredDocuments}
                onSubmitted={() => appFns?.reinitialiseUser?.()}
            />

            { shouldShowTierSelectionModal && (
                <TierSelectionModal
                    open={true}
                    onClose={() => setDismissedTierModal(true)}
                    tiers={props.availableTiers}
                    renewalPeriod={props.renewalPeriod}
                    shortTermDuration={props.shortTermDuration}
                    onSelect={(tierUuid) => props.onSelectTier(tierUuid)}
                    existingTier={props.licenseTier}
                    singleTierMode={!!props.licenseTier?.uuid}
                    renewalCurrency={props.currentTerms?.currentTerms?.renewalCurrency}
                />
            )}
        </>
    );
};

const LoadingContainer = ({ loadingCount, children }) => {
    const isFetching = loadingCount > 0;

    return (
        <>
            { isFetching && <PortalLoadingState><LottieLoadingIndicator /></PortalLoadingState>}
            { !isFetching && children }
        </>
    );
};

export default withRouter(BusinessDashboard);
